import React, { useState, useEffect } from "react";
import { useCanViewFinancials } from "../hooks/useCanViewFinancials";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../App";
import { ArrowLeft, Send, MessageSquare, Edit2, Save, X, Trash2, Paperclip, FileText, Download } from "lucide-react";
import StaleUpdateModal from "../components/StaleUpdateModal";
import { toast } from "sonner";
import { useAuth } from "../App";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import useShortcuts from "../hooks/useShortcuts";
import EditableField from "../components/ui/editable-field";
import { openAttachmentUrl } from "../lib/openAttachment";

// Rich-text editor for the Edit modal's description field. Same pattern used
// on the create page (`BizDevBriefs.jsx`), so briefs go in and out through
// matching editors — no more HTML-vs-plaintext corruption when someone edits.
let ReactQuill;
try { ReactQuill = require("react-quill-new").default; } catch (e) { ReactQuill = null; }
try { require("react-quill-new/dist/quill.snow.css"); } catch (e) { /* styles optional */ }

// Error boundary to prevent white screen crashes
class BriefErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("BriefDetail crash:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <h2 style={{ color: "var(--t1)", marginBottom: 8 }}>Something went wrong loading this brief</h2>
          <p style={{ color: "var(--t3)", fontSize: 13, marginBottom: 16 }}>{this.state.error?.message}</p>
          <button onClick={() => window.location.reload()} style={{ padding: "8px 20px", background: "var(--r)", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer" }}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import UserAutocomplete from "../components/UserAutocomplete";
import MentionTextarea from "../components/MentionTextarea";
import { celebrate } from "../hooks/useDMEngine";

import MobileSkeleton from "../components/MobileSkeleton";
import { AnimatedPage, AnimatedCard, SectionHeader } from "../BizDevAnimations";
import "../BizDevAnimations.css";

const STATUS_COLORS = {
  New: "#64748B", Assigned: "#06B6D4", "In Progress": "#8B5CF6", Pitched: "#A855F7",
  Won: "#22C55E", Executed: "#059669", Lost: "#6B7280", "On Hold": "#94A3B8",
};
const STATUSES = ["New", "Assigned", "In Progress", "Pitched", "Won", "Executed", "Lost", "On Hold"];

export default function BizDevBriefDetailWrapper() {
  return <BriefErrorBoundary><BizDevBriefDetail /></BriefErrorBoundary>;
}

function BizDevBriefDetail() {
  const { briefId } = useParams();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const canViewFinancials = useCanViewFinancials();
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [userNames, setUserNames] = useState({});
  const [tasks, setTasks] = useState([]);
  const [newTaskText, setNewTaskText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showStaleModal, setShowStaleModal] = useState(false);

  const { user } = useAuth();
  const roleKey = (r) => (r || "").toLowerCase();
  const canDelete = roleKey(user?.role) === "admin" || roleKey(user?.role) === "srmanager" || isSuperAdmin(user?.email);
  // Safe render: prevents "Objects are not valid as React child" crash (React error #31)
  const safe = (v) => (v === null || v === undefined) ? "" : (typeof v === "object" ? JSON.stringify(v) : String(v));

  useEffect(() => { loadBrief(); fetchTasks(); fetchAttachments(); }, [briefId]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Cmd/Ctrl+E toggles the advanced edit modal (for assignees / reviewer dates)
  useShortcuts(
    [
      { keys: "mod+e", description: "Edit brief (advanced)", group: "Brief",
        handler: () => setEditing((v) => !v) },
      { keys: "escape", description: "Close edit",
        handler: () => setEditing((v) => v ? false : v) },
    ],
    { enabled: !!brief && !showStaleModal }
  );

  // Single-field optimistic PATCH. <EditableField> throws on server error and
  // renders the message inline under the input, so we re-throw.
  const patchBrief = async (patch) => {
    const prev = brief;
    setBrief((b) => ({ ...(b || {}), ...patch }));
    try {
      const res = await api.put(`/bizdev/briefs/${briefId}`, patch);
      setBrief(res.data);
      if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
    } catch (e) {
      setBrief(prev);
      throw e;
    }
  };
  const fetchTasks = async () => {
    try {
      const res = await api.get(`/bizdev/briefs/${briefId}/tasks`);
      setTasks(res.data.tasks || []);
    } catch(e) { console.error("Failed to fetch tasks", e); }
  };

  const addTask = async () => {
    if (!newTaskText.trim()) return;
    try {
      await api.post(`/bizdev/briefs/${briefId}/tasks`, { text: newTaskText.trim() });
      setNewTaskText("");
      fetchTasks();
    } catch(e) { console.error("Failed to add task", e); }
  };

  const toggleTask = async (taskId, completed) => {
    try {
      await api.put(`/bizdev/briefs/${briefId}/tasks/${taskId}`, { completed: !completed });
      fetchTasks();
    } catch(e) { console.error("Failed to toggle task", e); }
  };

  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/bizdev/briefs/${briefId}/tasks/${taskId}`);
      fetchTasks();
    } catch(e) { console.error("Failed to delete task", e); }
  };

  const fetchAttachments = async () => {
    try { const r = await api.get(`/bizdev/briefs/${briefId}/attachments`); setAttachments(r.data || []); }
    catch(e) { console.error("Failed to fetch attachments", e); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/bizdev/briefs/${briefId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded: ${file.name}`);
      fetchAttachments();
    } catch(err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const loadBrief = async () => {
    try {
      const res = await api.get(`/bizdev/briefs/${briefId}`);
      // Sanitize: convert any object/array fields to strings to prevent React error #31
      const data = res.data;
      if (data) {
        for (const key of Object.keys(data)) {
          const v = data[key];
          if (v && typeof v === "object" && !Array.isArray(v) && key !== "structured_sections" && key !== "retainer_sow") {
            data[key] = JSON.stringify(v);
          }
        }
      }
      setBrief(data);
      if (res.data.stale && !showStaleModal) setShowStaleModal(true);
      // Resolve user IDs to names
      const ids = [res.data.assigned_to, res.data.sub_assigned_to, res.data.reviewer_1, res.data.reviewer_2].filter(Boolean);
      if (ids.length > 0) {
        try {
          const resolved = await api.post("/users/resolve", { ids });
          setUserNames(resolved.data || {});
        } catch (e) { console.error(e); }
      }
    } catch (e) {
      toast.error("Brief not found");
      navigate("/bizdev/briefs");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    // Guard: confirm before reverting Won/Executed
    const WON = ["Won", "Executed"];
    if (WON.includes(brief.status) && !WON.includes(status)) {
      if (!window.confirm(`This brief is marked "${brief.status}". Are you sure you want to revert it to "${status}"? This is unusual and will be logged.`)) return;
      try {
        const res = await api.put(`/bizdev/briefs/${briefId}`, { status, force_reopen: true });
        setBrief(res.data);
        toast.success(`Brief reopened → ${status}`);
        return;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed");
        return;
      }
    }
    try {
      const res = await api.put(`/bizdev/briefs/${briefId}`, { status });
      setBrief(res.data);
      toast.success(`Status: ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    try {
      const res = await api.post(`/bizdev/briefs/${briefId}/comments`, { text: comment });
      setBrief(res.data);
      setComment("");
      toast.success("Comment added");
    } catch (e) {
      toast.error("Failed");
    }
  };


  const startEdit = () => {
    setEditForm({
      title: brief.title || "", deep_brief: brief.deep_brief || "", status: brief.status || "New",
      pitch_date: brief.pitch_date || "", pitch_time: brief.pitch_time || "",
      pitch_location: brief.pitch_location || "", priority: brief.priority || "Medium",
      opportunity_value_lakhs: brief.opportunity_value_lakhs || "",
      service_type: brief.service_type || "",
      assigned_to: brief.assigned_to || "",
      sub_assigned_to: brief.sub_assigned_to || "",
      reviewer_1: brief.reviewer_1 || "",
      reviewer_2: brief.reviewer_2 || "",
      revenue_generated_lakhs: brief.revenue_generated_lakhs || "",
      revenue_attribution_month: brief.revenue_attribution_month || "",
      execution_month: brief.execution_month || "",
      review_1_date: brief.review_1_date || "", review_1_time: brief.review_1_time || "",
      review_2_date: brief.review_2_date || "", review_2_time: brief.review_2_time || "",
      pitch_deck_link: brief.pitch_deck_link || "",
      is_retainer: null, monthly_retainer_value: "", retainer_start_date: "", retainer_sow: null,
    });
    setEditing(true);
  };

  // Delete with 5s undo — no confirm modal. Navigate back immediately;
  // if user clicks Undo the delete never happens. Server DELETE fires on auto-close.
  const handleDeleteBrief = () => {
    let undone = false;
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(15);
    navigate("/bizdev/briefs");
    toast(`Deleted: ${(brief?.title || "brief").slice(0, 40)}`, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => { undone = true; navigate(`/bizdev/briefs/${briefId}`); },
      },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/bizdev/briefs/${briefId}`); }
          catch (e) { console.error("Delete failed", e); toast.error("Delete failed — brief still exists"); }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/bizdev/briefs/${briefId}`); }
          catch (e) { console.error("Delete failed", e); toast.error("Delete failed — brief still exists"); }
        }, 50);
      },
    });
  };

  // Delete a comment with undo (replaces window.confirm)
  const undoDeleteComment = (c) => {
    const commentId = c.comment_id;
    const snapshot = brief;
    // Optimistic: strip the comment (and its replies) locally
    setBrief((b) => ({
      ...(b || {}),
      comments: (b?.comments || []).filter(x => x.comment_id !== commentId && x.parent_comment_id !== commentId),
    }));
    let undone = false;
    toast(`Removed comment`, {
      duration: 5000,
      action: { label: "Undo", onClick: () => { undone = true; setBrief(snapshot); } },
      onAutoClose: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/bizdev/briefs/${briefId}/comments/${commentId}`); loadBrief(); }
          catch (e) { setBrief(snapshot); toast.error("Delete failed — restored"); }
        }, 50);
      },
      onDismiss: () => {
        if (undone) return;
        setTimeout(async () => {
          try { await api.delete(`/bizdev/briefs/${briefId}/comments/${commentId}`); loadBrief(); }
          catch (e) { setBrief(snapshot); toast.error("Delete failed — restored"); }
        }, 50);
      },
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.assigned_to) { toast.error("Assigned To is required"); return; }
    if (!editForm.sub_assigned_to) { toast.error("Sub Assigned To is required"); return; }
    if (!editForm.reviewer_1) { toast.error("Reviewer 1 is required"); return; }
    if (!editForm.reviewer_2) { toast.error("Reviewer 2 is required"); return; }
    if (!editForm.review_1_date || !editForm.review_1_time) { toast.error("Review 1 Date & Time are required"); return; }
    if (!editForm.review_2_date || !editForm.review_2_time) { toast.error("Review 2 Date & Time are required"); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) {
          payload[k] = ["opportunity_value_lakhs", "revenue_generated_lakhs"].includes(k) ? parseFloat(v) : v;
        }
      });
      const res = await api.put(`/bizdev/briefs/${briefId}`, payload);
      setBrief(res.data);
      // If Won/Executed, update lead with retainer info
      if (["Won", "Executed"].includes(editForm.status) && brief.lead_id) {
        try {
          const leadUpdate = { is_retainer: !!editForm.is_retainer };
          if (editForm.is_retainer) {
            if (editForm.monthly_retainer_value) leadUpdate.monthly_retainer_value = parseFloat(editForm.monthly_retainer_value) * 100000;
            if (editForm.retainer_start_date) leadUpdate.retainer_start_date = editForm.retainer_start_date;
            if (editForm.retainer_sow) leadUpdate.retainer_sow = editForm.retainer_sow;
          }
          await api.put(`/leads/${brief.lead_id}`, leadUpdate);
        } catch (e) { console.error("Failed to update lead retainer info", e); }
      }
      setEditing(false);
      // Celebration for Won briefs
      if (["Won", "Executed"].includes(editForm.status) && !["Won", "Executed"].includes(brief.status)) {
        celebrate(`🎉 BRIEF WON: ${brief.title || "Untitled"}`);
      } else {
        toast.success("Brief updated");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return window.innerWidth < 768 ? <MobileSkeleton type="detail" /> : <><div className="flex items-center justify-center h-64"><div className="animate-pulse text-cyan-400 font-heading text-xl">LOADING...</div></div></>;
  }
  if (!brief) return null;
  if (showStaleModal) return <StaleUpdateModal entityType="brief" entityId={briefId} entityTitle={brief.title} onSave={() => { setShowStaleModal(false); loadBrief(); }} />;

  return (
    <>
      <div data-testid="bizdev-brief-detail" className="space-y-5 max-w-4xl">
        <TrainingBriefSection brief={brief} briefId={briefId} onUpdate={loadBrief} />
        {/* Header */}
        <div className="dm-detail-header flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 text-text-muted hover:text-[var(--t1)]"><ArrowLeft size={20} /></button>
          <div className="flex-1 min-w-0">
            {/* Title: inline-editable. Click / tap title to edit. Enter saves, Esc cancels. */}
            <EditableField
              value={safe(brief.title)}
              onSave={(v) => patchBrief({ title: v })}
              placeholder="Untitled brief"
              size="lg"
              className="font-heading text-2xl text-[var(--t1)] tracking-tight"
              inputClassName="font-heading text-2xl tracking-tight"
            />
            <p className="text-text-muted text-xs mt-0.5">{safe(brief.company_name)} {brief.opportunity_value_lakhs ? `| ₹${safe(brief.opportunity_value_lakhs)}L` : ""}</p>
          </div>
          <div className="dm-detail-actions flex items-center gap-2">
          <button onClick={() => navigate(`/tasks/new?brief_id=${briefId}`)} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--r)] rounded-xl text-white text-xs font-bold hover:bg-[var(--r)] transition-colors">
            + Task
          </button>
          <button onClick={startEdit} title="Advanced edit (Cmd+E) — assignees & review dates" className="flex items-center gap-1.5 px-3 py-1.5 glass rounded-xl text-[var(--t2)] text-xs hover:text-[var(--t1)] hover:bg-[var(--s3)] transition-colors" data-testid="edit-brief-btn">
            <Edit2 size={14} /> Edit
          </button>
          {canDelete && <button onClick={handleDeleteBrief} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl text-[#888] text-xs hover:text-red-400 hover:border-red-400/30 transition-colors" data-testid="delete-brief-btn">
            <Trash2 size={14} /> Delete
          </button>}
          </div>
        </div>

        {/* Star + Inline Status + Assignment Info */}
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={async () => { await api.post(`/bizdev/briefs/${briefId}/star`); loadBrief(); }} data-testid="star-brief"
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${brief.starred ? "bg-yellow-400/20 text-yellow-400" : "bg-[var(--s1)] border border-[var(--bdr)] text-[#666] hover:text-yellow-400"}`}>
            {brief.starred ? "★ Urgent" : "☆ Star"}
          </button>
          {STATUSES.map(s => (
            <button key={s} onClick={async () => {
              if (s === "Assigned" && !brief.assigned_to) { startEdit(); return; }
              try { await api.put(`/bizdev/briefs/${briefId}`, { status: s }); loadBrief(); toast.success(`Status: ${s}`); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${brief.status === s ? "text-white" : "text-[#888] hover:text-[var(--t1)]/80"}`}
              style={brief.status === s ? { backgroundColor: (STATUS_COLORS[s] || "#666") + "25", color: STATUS_COLORS[s] || "#666" } : {}}>
              {s}
            </button>
          ))}
        </div>

        {/* Key Details — inline-editable single-field PATCH. No Edit modal round-trip. */}
        <div className="glass-card p-4">
          <p className="text-[11px] font-mono text-[var(--t3)] tracking-widest mb-3">KEY DETAILS</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-4 text-sm">
            <EditableField
              label="Priority"
              type="select"
              value={brief.priority || ""}
              options={[{ value: "", label: "—" }, ...["Low","Medium","High","Critical"].map(p => ({ value: p, label: p }))]}
              onSave={(v) => patchBrief({ priority: v || null })}
              className="text-white/80"
            />
            <EditableField
              label="Service Type"
              type="select"
              value={brief.service_type || ""}
              options={[
                { value: "", label: "—" },
                ...["Social & Digital Retainer","Mainline Ad Film Production","Digital Campaign","Digital Content Production","Influencer Marketing","Media Buying"].map(s => ({ value: s, label: s })),
              ]}
              onSave={(v) => patchBrief({ service_type: v || null })}
              className="text-white/80"
            />
            {canViewFinancials && (
              <EditableField
                label="Opportunity (₹L)"
                type="number"
                value={brief.opportunity_value_lakhs ?? ""}
                onSave={(v) => patchBrief({ opportunity_value_lakhs: v === "" ? null : parseFloat(v) })}
                displayFormatter={(v) => v ? `₹${v}L` : "—"}
                className="text-white/80"
                emptyText="Set value"
              />
            )}
            <EditableField
              label="Pitch Date"
              type="date"
              value={brief.pitch_date || ""}
              onSave={(v) => patchBrief({ pitch_date: v || null })}
              displayFormatter={(v) => v ? new Date(v + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "No date"}
              className="text-white/80"
              emptyText="Set date"
            />
            <EditableField
              label="Pitch Time"
              type="select"
              value={brief.pitch_time || ""}
              options={[
                { value: "", label: "No time" },
                ...["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30","20:00"].map(t => {
                  const [h,m] = t.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                  return { value: t, label: `${hr12}:${m} ${ampm}` };
                }),
              ]}
              onSave={(v) => patchBrief({ pitch_time: v || null })}
              displayFormatter={(v) => {
                if (!v) return "—";
                const [h,m] = v.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                return `${hr12}:${m} ${ampm}`;
              }}
              className="text-white/80"
              emptyText="Set time"
            />
            <EditableField
              label="Pitch Location"
              value={brief.pitch_location || ""}
              onSave={(v) => patchBrief({ pitch_location: v || null })}
              className="text-white/80"
              emptyText="Set location"
            />
            <EditableField
              label="Pitch Deck Link"
              type="url"
              value={brief.pitch_deck_link || ""}
              onSave={(v) => patchBrief({ pitch_deck_link: v || null })}
              displayFormatter={(v) => v ? v.replace(/^https?:\/\//, "").slice(0, 40) + (v.length > 47 ? "…" : "") : "—"}
              className="text-white/80"
              emptyText="Add link"
            />
          </div>
          <p className="text-[10px] text-[var(--t3)] opacity-60 pt-3">Click any field to edit · Cmd+E opens advanced edit (people &amp; review dates)</p>
        </div>

        {/* Assignment Info */}
        {(brief.assigned_to || brief.reviewer_1) && (
          <div className="glass-card p-4 flex flex-wrap gap-4 text-sm">
            {[
              { label: "Assigned To", id: brief.assigned_to },
              { label: "Sub Assigned", id: brief.sub_assigned_to },
              { label: "Reviewer 1", id: brief.reviewer_1 },
              { label: "Reviewer 2", id: brief.reviewer_2 },
            ].filter(r => r.id).map(r => {
              const resolved = userNames[r.id];
              return (
                <button key={r.label} onClick={() => navigate(`/team/${r.id}`)} className="text-left hover:bg-[var(--s2)] p-2.5 rounded-lg transition-colors">
                  <p className="text-[#888] text-xs">{r.label}</p>
                  <p className="text-[var(--t1)] font-medium">{resolved?.name || r.id}</p>
                  {resolved?.designation && <p className="text-[#888] text-xs">{resolved.designation}</p>}
                </button>
              );
            })}
          </div>
        )}

        
        {/* Review Dates & Times */}
        {(brief.review_1_date || brief.review_2_date) && (
          <div className="glass-card p-4 flex flex-wrap gap-4 text-sm" style={{marginTop:"12px"}}>
            {brief.review_1_date && (
              <div>
                <p className="text-[#888] text-xs">Review 1</p>
                <p className="text-[var(--t1)] font-medium">{safe(brief.review_1_date)}{brief.review_1_time ? ` at ${safe(brief.review_1_time)}` : ""}</p>
              </div>
            )}
            {brief.review_2_date && (
              <div>
                <p className="text-[#888] text-xs">Review 2</p>
                <p className="text-[var(--t1)] font-medium">{safe(brief.review_2_date)}{brief.review_2_time ? ` at ${safe(brief.review_2_time)}` : ""}</p>
              </div>
            )}
          </div>
        )}

        {/* Edit Modal */}
        {editing && (
          <div className="glass-card rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-semibold text-sm">EDIT BRIEF</h3>
              <button onClick={() => setEditing(false)} className="text-text-muted hover:text-[var(--t1)]"><X size={16} /></button>
            </div>
            <input value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} placeholder="Title" className="form-input" />
            <div>
              <label className="text-[var(--t2)] text-sm mb-1 block">Description / Brief</label>
              {ReactQuill ? (
                <div className="dm-brief-edit-quill" style={{ background: "white", borderRadius: 8 }}>
                  <ReactQuill
                    theme="snow"
                    value={editForm.deep_brief || ""}
                    onChange={(v) => setEditForm({ ...editForm, deep_brief: v })}
                    placeholder="Brief description..."
                  />
                </div>
              ) : (
                <textarea value={editForm.deep_brief || ""} onChange={(e) => setEditForm({ ...editForm, deep_brief: e.target.value })} placeholder="Brief description..." className="form-input" rows={4} style={{ resize: "vertical" }} />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Status</label>
                <select value={editForm.status} onChange={(e) => setEditForm({...editForm, status: e.target.value})} className="form-input bg-transparent cursor-pointer">
                  {STATUSES.map(s => <option key={s} value={s} className="bg-black">{s}</option>)}
                </select>
              </div>
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Priority</label>
                <select value={editForm.priority} onChange={(e) => setEditForm({...editForm, priority: e.target.value})} className="form-input bg-transparent cursor-pointer">
                  {["Low","Medium","High","Critical"].map(p => <option key={p} value={p} className="bg-black">{p}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Opportunity (₹L)</label>
                <input type="number" step="any" value={editForm.opportunity_value_lakhs} onChange={(e) => setEditForm({...editForm, opportunity_value_lakhs: e.target.value})} className="form-input" />
              </div>
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Service Type</label>
                <select value={editForm.service_type} onChange={(e) => setEditForm({...editForm, service_type: e.target.value})} className="form-input bg-transparent cursor-pointer">
                  <option value="" className="bg-black">Select</option>
                  {["Social & Digital Retainer","Mainline Ad Film Production","Digital Campaign","Digital Content Production","Influencer Marketing","Media Buying"].map(s => <option key={s} value={s} className="bg-black">{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Pitch Date</label><input type="date" value={editForm.pitch_date} onChange={(e) => setEditForm({...editForm, pitch_date: e.target.value})} className="form-input" /></div>
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Pitch Time</label>
                <select value={editForm.pitch_time} onChange={(e) => setEditForm({...editForm, pitch_time: e.target.value})} className="form-input bg-transparent">
                  <option value="">No time</option>
                  {["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30","20:00"].map(t => {
                    const [h,m] = t.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                    return <option key={t} value={t}>{`${hr12}:${m} ${ampm}`}</option>;
                  })}
                </select>
              </div>
            </div>
            <input value={editForm.pitch_location} onChange={(e) => setEditForm({...editForm, pitch_location: e.target.value})} placeholder="Pitch Location" className="form-input" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Review 1 Date *</label><input type="date" value={editForm.review_1_date} onChange={(e) => setEditForm({...editForm, review_1_date: e.target.value})} className="form-input" required /></div>
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Review 1 Time *</label>
                <select value={editForm.review_1_time} onChange={(e) => setEditForm({...editForm, review_1_time: e.target.value})} className="form-input bg-transparent">
                  <option value="">No time</option>
                  {["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30","20:00"].map(t => {
                    const [h,m] = t.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                    return <option key={t} value={t}>{`${hr12}:${m} ${ampm}`}</option>;
                  })}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Review 2 Date *</label><input type="date" value={editForm.review_2_date} onChange={(e) => setEditForm({...editForm, review_2_date: e.target.value})} className="form-input" required /></div>
              <div><label className="text-[var(--t2)] text-sm mb-1 block">Review 2 Time *</label>
                <select value={editForm.review_2_time} onChange={(e) => setEditForm({...editForm, review_2_time: e.target.value})} className="form-input bg-transparent">
                  <option value="">No time</option>
                  {["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30","19:00","19:30","20:00"].map(t => {
                    const [h,m] = t.split(":"); const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM"; const hr12 = hr > 12 ? hr-12 : hr === 0 ? 12 : hr;
                    return <option key={t} value={t}>{`${hr12}:${m} ${ampm}`}</option>;
                  })}
                </select>
              </div>
            </div>
            <input value={editForm.pitch_deck_link} onChange={(e) => setEditForm({...editForm, pitch_deck_link: e.target.value})} placeholder="Pitch Deck Link (URL)" className="form-input" />
            {/* Assignment fields */}
            <UserAutocomplete value={editForm.assigned_to} onChange={(v) => setEditForm({...editForm, assigned_to: v})} label="Assigned To *" placeholder="Primary owner..." required />
            <UserAutocomplete value={editForm.sub_assigned_to} onChange={(v) => setEditForm({...editForm, sub_assigned_to: v})} label="Sub Assigned To *" placeholder="Secondary support..." required />
            <UserAutocomplete value={editForm.reviewer_1} onChange={(v) => setEditForm({...editForm, reviewer_1: v})} label="Reviewer 1 *" placeholder="Approval owner..." required />
            <UserAutocomplete value={editForm.reviewer_2} onChange={(v) => setEditForm({...editForm, reviewer_2: v})} label="Reviewer 2 *" placeholder="Backup reviewer..." required />
            {/* Revenue fields for Won/Executed */}
            {(editForm.status === "Won" || editForm.status === "Executed") && (
              <div className="p-3 rounded-xl border border-green-500/20 bg-green-500/5 space-y-3">
                <p className="text-green-400 text-xs font-medium">Revenue Attribution (required)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><label className="text-[var(--t2)] text-sm mb-1 block">Revenue Generated (₹L) *</label>
                    <input type="number" step="any" value={editForm.revenue_generated_lakhs} onChange={(e) => setEditForm({...editForm, revenue_generated_lakhs: e.target.value})} className="form-input" required />
                    {editForm.opportunity_value_lakhs && !editForm.revenue_generated_lakhs && (
                      <button type="button" onClick={() => setEditForm({...editForm, revenue_generated_lakhs: editForm.opportunity_value_lakhs})} className="text-cyan-400 text-[12px] mt-1 hover:underline">Use opportunity ({editForm.opportunity_value_lakhs}L)?</button>
                    )}
                  </div>
                  <div><label className="text-[var(--t2)] text-sm mb-1 block">Execution Month *</label>
                    <input type="month" value={editForm.execution_month} onChange={(e) => setEditForm({...editForm, execution_month: e.target.value})} className="form-input" required />
                  </div>
                </div>
                <div><label className="text-[var(--t2)] text-sm mb-1 block">Revenue Attribution Month *</label>
                  <input type="month" value={editForm.revenue_attribution_month} onChange={(e) => setEditForm({...editForm, revenue_attribution_month: e.target.value})} className="form-input" required />
                </div>
              </div>
            )}
            {/* Retainer Setup for Won */}
            {(editForm.status === "Won" || editForm.status === "Executed") && (
              <div className="p-3 rounded-xl border border-indigo-500/20 bg-indigo-500/5 space-y-3">
                <p className="text-indigo-400 text-xs font-medium">Account Type</p>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setEditForm({...editForm, is_retainer: true})}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${editForm.is_retainer === true ? "bg-indigo-500/20 border-indigo-500 text-indigo-300" : "border-[var(--bdrh)] text-[#666] hover:border-[#555]"}`}>
                    Retainer
                  </button>
                  <button type="button" onClick={() => setEditForm({...editForm, is_retainer: false, monthly_retainer_value: "", retainer_sow: null})}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${editForm.is_retainer === false ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-[var(--bdrh)] text-[#666] hover:border-[#555]"}`}>
                    POC / Campaign
                  </button>
                </div>
                {editForm.is_retainer === true && (
                  <div className="space-y-3 mt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div><label className="text-[var(--t2)] text-sm mb-1 block">Monthly Fee (₹L) *</label>
                        <input type="number" step="any" value={editForm.monthly_retainer_value || ""} onChange={(e) => setEditForm({...editForm, monthly_retainer_value: e.target.value})} className="form-input" placeholder="e.g., 22.76" />
                      </div>
                      <div><label className="text-[var(--t2)] text-sm mb-1 block">Start Date</label>
                        <input type="date" value={editForm.retainer_start_date || ""} onChange={(e) => setEditForm({...editForm, retainer_start_date: e.target.value})} className="form-input" />
                      </div>
                    </div>
                    <p className="text-indigo-400/60 text-xs">Monthly SOW (deliverables per month)</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {["reels", "statics", "stories", "carousels", "influencers"].map(t => (
                        <div key={t}>
                          <label className="text-[var(--t2)] text-xs capitalize block mb-0.5">{t}</label>
                          <input type="number" min="0" value={(editForm.retainer_sow || {})[t] || ""} onChange={(e) => setEditForm({...editForm, retainer_sow: {...(editForm.retainer_sow || {}), [t]: parseInt(e.target.value) || 0}})} className="form-input text-center text-sm" placeholder="0" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {editForm.is_retainer === false && (
                  <p className="text-amber-400/50 text-xs">This account will be tracked as a POC/Campaign for retainer conversion</p>
                )}
              </div>
            )}
            {/* Needs attribution warning for old Won/Executed briefs */}
            {brief.status && ["Won","Executed"].includes(brief.status) && !brief.revenue_generated_lakhs && (
              <div className="p-3 rounded-xl border border-yellow-500/20 bg-yellow-500/5">
                <p className="text-yellow-400 text-xs font-medium">This brief needs revenue attribution to count in dashboards</p>
              </div>
            )}
            <button onClick={handleSaveEdit} disabled={saving} className="w-full py-3 bg-[var(--r)] text-white font-bold rounded-xl hover:bg-[var(--r)] transition-colors disabled:opacity-50">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}

        {/* Needs Attribution Banner */}
        {!editing && brief.status && ["Won","Executed"].includes(brief.status) && !brief.revenue_generated_lakhs && (
          <div className="p-3 rounded-xl border border-yellow-500/20 bg-yellow-500/5 flex items-center justify-between">
            <p className="text-yellow-400 text-xs font-medium">Needs revenue attribution — excluded from dashboard totals</p>
            <button onClick={startEdit} className="text-yellow-400 text-xs hover:underline">Fix now</button>
          </div>
        )}

        {/* Status + Priority */}
        <div className="flex flex-wrap gap-2">
          <div className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: (STATUS_COLORS[brief.status] || "#64748B") + "20", color: STATUS_COLORS[brief.status] || "#64748B" }}>
            {safe(brief.status)}
          </div>
          <div className={`px-3 py-1.5 rounded-full text-xs font-medium ${brief.priority === "Critical" ? "bg-red-500/20 text-red-400" : brief.priority === "High" ? "bg-yellow-500/20 text-yellow-400" : "bg-zinc-500/20 text-zinc-400"}`}>
            {safe(brief.priority)}
          </div>
          {brief.service_type && <div className="px-3 py-1.5 rounded-full text-xs bg-cyan-500/10 text-cyan-400">{safe(brief.service_type)}</div>}
          {brief.revenue_type && <div className="px-3 py-1.5 rounded-full text-xs bg-[var(--s2)] text-[var(--t2)]">{safe(brief.revenue_type)}</div>}
        </div>

        {/* Auto-structured "Brief Doc" block removed — used to be AI-generated
            (openai/gpt-5) on every brief create; that AI call was ripped out
            to eliminate create-time latency. Old briefs may still have
            structured_sections data in Mongo but we no longer render it. */}

        {/* Brief content — rendered as HTML (Quill output from create + edit modal).
            Use the Edit button in the top bar to modify. */}
        {brief.deep_brief && (
          <div className="glass-card rounded-2xl p-5">
            <p className="text-[var(--t2)] text-xs mb-2 font-heading tracking-wider">BRIEF</p>
            {typeof brief.deep_brief === "string" ? (
              <div className="text-[var(--t2)] text-sm normal-case font-normal max-h-96 overflow-y-auto custom-scrollbar prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: brief.deep_brief }} />
            ) : (
              <pre className="text-[var(--t2)] text-sm whitespace-pre-wrap">{JSON.stringify(brief.deep_brief, null, 2)}</pre>
            )}
          </div>
        )}

        {/* Status Pipeline */}
        <div className="bg-surface border border-border rounded-xl p-4">
          <p className="text-text-muted text-xs mb-2 font-heading tracking-wider">MOVE STATUS</p>
          <div className="flex flex-wrap gap-1">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => updateStatus(s)} className={`px-2.5 py-1 text-[13px] rounded-lg transition-all ${brief.status === s ? "font-bold" : "hover:bg-[var(--s2)]"}`} style={{ backgroundColor: brief.status === s ? (STATUS_COLORS[s] || "#64748B") + "30" : undefined, color: brief.status === s ? STATUS_COLORS[s] : "#71717A" }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        {brief.description && (
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs mb-1">DESCRIPTION</p>
            <p className="text-text-secondary text-sm">{safe(brief.description)}</p>
          </div>
        )}

        {/* Pitch Details */}
        {(brief.pitch_date || brief.pitch_time || brief.pitch_location) && (
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs mb-2 font-heading tracking-wider">PITCH DETAILS</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {brief.pitch_date && <div><p className="text-text-muted text-xs">Date</p><p className="text-[var(--t1)]">{safe(brief.pitch_date)}</p></div>}
              {brief.pitch_time && <div><p className="text-text-muted text-xs">Time</p><p className="text-[var(--t1)]">{safe(brief.pitch_time)}</p></div>}
              {brief.pitch_location && <div><p className="text-text-muted text-xs">Location</p><p className="text-[var(--t1)]">{safe(brief.pitch_location)}</p></div>}
            </div>
          </div>
        )}


        
        {/* Attachments */}
        <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--t3)", letterSpacing: ".1em", display: "flex", alignItems: "center", gap: 4 }}><Paperclip size={12} /> ATTACHMENTS {attachments.length > 0 && <span style={{ color: "var(--t2)" }}>({attachments.length})</span>}</p>
            <label style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8, background: "var(--s3)", color: "var(--t2)", cursor: uploading ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
              <Paperclip size={12} /> {uploading ? "Uploading..." : "Add File"}
              <input type="file" onChange={handleFileUpload} disabled={uploading} style={{ display: "none" }} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp" />
            </label>
          </div>
          {attachments.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--t3)", textAlign: "center", padding: "12px 0" }}>No attachments yet. Upload briefs, decks, or documents.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {attachments.map(att => (
                <div key={att.attachment_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--bdr)", background: "var(--s2)" }}>
                  <FileText size={16} style={{ color: "var(--t3)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: "var(--t1)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.filename}</span>
                    <span style={{ fontSize: 11, color: "var(--t3)" }}>{att.uploaded_by_name || "Unknown"} · {att.created_at ? new Date(att.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}</span>
                  </div>
                  {att.file_url && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openAttachmentUrl(att.file_url, att.filename); }}
                      title="Download"
                      style={{ background: "transparent", border: "none", color: "var(--c)", cursor: "pointer", padding: 4, display: "flex" }}
                    >
                      <Download size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comments */}
        <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 16 }}>
          <div className="flex items-baseline justify-between mb-3">
            <p style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--t3)", letterSpacing: ".1em", display: "flex", alignItems: "center", gap: 4 }}><MessageSquare size={12} /> COMMENTS</p>
            <span className="text-[10px] text-[var(--t3)] opacity-70">Enter to post · Shift+Enter for newline</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <MentionTextarea value={comment} onChange={setComment} onSubmit={handleComment}
              placeholder="Add a comment... (type @ to mention)" rows={1}
              className="flex-1 px-4 py-2 bg-[var(--s1)] border border-[var(--bdr)] rounded-xl text-white text-sm placeholder:text-[#888] focus:border-cyan-500/50 focus:outline-none resize-none" />
          </div>
          <div className="space-y-2">
            {(brief.comments || []).filter(c => !c.parent_comment_id).slice().reverse().map((c) => {
              const isOwn = c.author_id === (user?.user_id || user?.id);
              const replies = (brief.comments || []).filter(r => r.parent_comment_id === c.comment_id);
              return (
                <div key={c.comment_id} className="p-3 rounded-lg" style={{ background: "var(--s2)", border: "1px solid var(--bdr)" }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white/80 text-xs font-medium">{c.author_name}</span>
                    <div className="flex items-center gap-2">
                      {c.edited_at && <span className="text-[#666] text-xs">(edited)</span>}
                      <span className="text-[#666] text-xs">{new Date(c.created_at).toLocaleDateString()}</span>
                      {isOwn && (
                        <button onClick={() => undoDeleteComment(c)} className="text-[#666] hover:text-red-400 text-xs">Delete</button>
                      )}
                    </div>
                  </div>
                  <p className="text-[var(--t2)] text-sm">{c.text}</p>
                  {/* Replies */}
                  {replies.length > 0 && (
                    <div className="ml-4 mt-2 space-y-2 border-l border-[var(--bdr)] pl-3">
                      {replies.map(r => (
                        <div key={r.comment_id} className="text-xs">
                          <span className="text-[var(--t2)] font-medium">{r.author_name}:</span>
                          <span className="text-[var(--t2)] ml-1">{r.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {(brief.comments || []).length === 0 && <p className="text-[#888] text-xs text-center py-4">No comments yet</p>}
          </div>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════
// TRAINING BRIEF SECTION
// Banner + score modal — shown only on training assessments
// Also shows toggle for admin to mark a brief as training assessment
// ════════════════════════════════════════
function TrainingBriefSection({ brief, briefId, onUpdate }) {
  const { user: currentUser } = useAuth();
  const [showScore, setShowScore] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [pillars, setPillars] = useState([]);
  const [users, setUsers] = useState([]);
  const isTraining = brief.is_training_assessment;
  const scored = brief.training_score !== null && brief.training_score !== undefined;
  const passed = brief.training_passed;
  // Scorer eligibility: admin, brief's manager/reviewers, but NOT the trainee themselves
  const currentUid = currentUser?.user_id || currentUser?.id;
  const isAdminUser = currentUser?.role?.toLowerCase() === "admin";
  const isTrainee = brief.training_assignee_id === currentUid;
  const isManager = [brief.assigned_to, brief.sub_assigned_to, brief.reviewer_1, brief.reviewer_2, brief.created_by].includes(currentUid);
  const canScore = (isAdminUser || isManager) && !isTrainee;

  React.useEffect(() => {
    if (showLink) {
      api.get("/onboarding/pillars").then(r => setPillars(r.data || [])).catch(() => {});
      api.get("/team-members").then(r => setUsers(r.data || [])).catch(() => {});
    }
  }, [showLink]);

  if (!isTraining && !isAdminUser) return null;

  return (
    <>
      {isTraining && (
        <div style={{ background: "linear-gradient(135deg, rgba(99,102,241,.1), rgba(139,92,246,.1))", border: "1px solid rgba(99,102,241,.3)", borderRadius: 10, padding: 14 }}>
          <div className="flex items-center gap-3">
            <span style={{ fontSize: 22 }}>🎓</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[var(--t1)]">Training Assessment</p>
              <p className="text-xs text-[var(--t3)]">
                {scored
                  ? `Scored ${brief.training_score}/100 by manager · ${passed ? "✅ Passed" : "❌ Failed (retry needed)"}`
                  : "Awaiting manager score (≥80 to pass)"}
              </p>
              {scored && brief.training_score_notes && (
                <p className="text-xs text-[var(--t2)] mt-2 italic">"{brief.training_score_notes}"</p>
              )}
              {isTrainee && !scored && (
                <p className="text-xs text-[var(--a)] mt-2">This is your training assignment. Submit your work; your manager will score it.</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!scored && canScore && (
                <button onClick={() => setShowScore(true)}
                  className="px-3 py-1.5 bg-[var(--r)] text-white text-xs font-bold rounded-lg">
                  Score 0-100
                </button>
              )}
              {scored && canScore && (
                <button onClick={() => setShowScore(true)}
                  className="px-3 py-1.5 bg-[var(--s2)] border border-[var(--bdr)] text-[var(--t2)] text-xs font-bold rounded-lg">
                  Re-score
                </button>
              )}
              {isAdminUser && (
                <button onClick={async () => {
                  if (!window.confirm("Unlink this brief from training? The score history will be kept for audit but it won't count as a training assessment anymore.")) return;
                  try {
                    await api.post(`/onboarding/briefs/${briefId}/unlink`);
                    toast.success("Unlinked from training");
                    onUpdate();
                  } catch { toast.error("Failed to unlink"); }
                }}
                  className="px-2 py-1.5 bg-[var(--s2)] border border-[var(--bdr)] text-[var(--t3)] text-xs rounded-lg hover:text-[var(--r)]"
                  title="Unlink from training">
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!isTraining && isAdminUser && (
        <div className="flex justify-end">
          <button onClick={() => setShowLink(true)}
            className="text-xs px-3 py-1 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-[var(--t3)] hover:text-[var(--t1)]">
            🎓 Mark as training assessment
          </button>
        </div>
      )}

      {showScore && <ScoreModal brief={brief} briefId={briefId} onClose={() => setShowScore(false)} onSaved={() => { setShowScore(false); onUpdate(); }} />}
      {showLink && <LinkTrainingModal brief={brief} briefId={briefId} pillars={pillars} users={users} onClose={() => setShowLink(false)} onSaved={() => { setShowLink(false); onUpdate(); }} />}
    </>
  );
}

function ScoreModal({ brief, briefId, onClose, onSaved }) {
  const [score, setScore] = useState(brief.training_score || 75);
  const [notes, setNotes] = useState(brief.training_score_notes || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/onboarding/briefs/${briefId}/score`, { score: parseInt(score), notes });
      toast.success(score >= 80 ? "✅ Trainee passed!" : "❌ Marked as failed (trainee can retry)");
      onSaved();
    } catch (e) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const passed = score >= 80;

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 24, width: "min(440px, 92vw)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[var(--t1)]">Score Training Brief</h3>
          <button onClick={onClose} className="p-1 text-[var(--t3)]"><X size={16} /></button>
        </div>
        <p className="text-xs text-[var(--t3)] mb-4">{brief.title}</p>

        <div className="text-center mb-4">
          <div className="font-heading text-5xl mb-2" style={{ color: passed ? "var(--g)" : "var(--r)" }}>{score}</div>
          <p className="text-xs text-[var(--t3)]">{passed ? "✅ Pass (≥80)" : "❌ Fail (<80)"}</p>
        </div>

        <input type="range" min={0} max={100} value={score} onChange={e => setScore(e.target.value)}
          className="w-full mb-4" style={{ accentColor: passed ? "var(--g)" : "var(--r)" }} />

        <div className="grid grid-cols-5 gap-1 mb-4">
          {[20, 40, 60, 80, 100].map(v => (
            <button key={v} onClick={() => setScore(v)}
              className="px-2 py-1 bg-[var(--s2)] border border-[var(--bdr)] rounded text-xs text-[var(--t2)] hover:text-[var(--t1)]">
              {v}
            </button>
          ))}
        </div>

        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Feedback for trainee..."
          rows={3} className="w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none mb-4" />

        <button onClick={submit} disabled={saving}
          className="w-full py-2.5 bg-[var(--r)] text-white font-bold rounded-lg text-sm disabled:opacity-50">
          {saving ? "Saving..." : `Save Score: ${score}/100`}
        </button>
      </div>
    </div>
  );
}

function LinkTrainingModal({ brief, briefId, pillars, users, onClose, onSaved }) {
  const [pillarId, setPillarId] = useState("");
  const [traineeId, setTraineeId] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!pillarId || !traineeId) return toast.error("Pick a pillar and trainee");
    setSaving(true);
    try {
      await api.put(`/bizdev/briefs/${briefId}`, {
        is_training_assessment: true,
        training_pillar_id: pillarId,
        training_assignee_id: traineeId,
      });
      toast.success("Linked as training assessment");
      onSaved();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 24, width: "min(440px, 92vw)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[var(--t1)]">Link as Training Assessment</h3>
          <button onClick={onClose} className="p-1 text-[var(--t3)]"><X size={16} /></button>
        </div>
        <p className="text-xs text-[var(--t3)] mb-4">This brief becomes a training assignment for a trainee. You'll score it 0-100 when they submit.</p>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-[var(--t2)] block mb-1">Pillar</label>
            <select value={pillarId} onChange={e => setPillarId(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none">
              <option value="">Select a pillar...</option>
              {pillars.map(p => <option key={p.pillar_id} value={p.pillar_id}>{p.icon} {p.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--t2)] block mb-1">Trainee</label>
            <select value={traineeId} onChange={e => setTraineeId(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--s2)] border border-[var(--bdr)] rounded-lg text-sm text-[var(--t1)] outline-none">
              <option value="">Select a trainee...</option>
              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
        </div>

        <button onClick={submit} disabled={saving}
          className="w-full mt-4 py-2.5 bg-[var(--r)] text-white font-bold rounded-lg text-sm disabled:opacity-50">
          {saving ? "Saving..." : "Link as Training Assessment"}
        </button>
      </div>
    </div>
  );
}
